/*
  Gemeinsames Einstellungen-Panel für Shiftplan V2.
*/

import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import { IconColor, IconSettings } from "../../../../design-system/icons";
import ShiftplanDialog from "../ui/ShiftplanDialog";
import { AppearanceSettingsContent } from "./AppearanceSettingsPanel";
import { OrderSettingsContent } from "./OrderSettingsPanel";
import { PublicHolidaysContent } from "./PublicHolidaysPanel";

const SETTINGS_SECTIONS = Object.freeze([
  {
    id: "order",
    label: "Reihenfolge",
    icon: <FormatListNumberedOutlinedIcon fontSize="small" />,
  },
  {
    id: "holidays",
    label: "Feiertage",
    icon: <CelebrationOutlinedIcon fontSize="small" />,
  },
  {
    id: "appearance",
    label: "Darstellung",
    icon: <IconColor fontSize="small" />,
  },
]);

function normalizeSection(section, sections) {
  return sections.some((item) => item.id === section) ? section : sections[0].id;
}

export default function SettingsPanel({
  open,
  activeSection = "order",
  initialOrderMode,
  locationName = "",
  employees,
  departments,
  colorDepartments,
  shiftTemplates,
  referenceDate,
  displayCalendar,
  shiftDisplayMode,
  onClose,
  onSaveEmployeeOrder,
  onResetEmployeeOrder,
  onSaveDepartmentOrder,
  onResetDepartmentOrder,
  onPublicHolidaysChanged,
  onDisplayCalendarChange,
  onDepartmentColorChange,
  onShiftColorChange,
  onShiftDisplayModeChange,
  // Toast der Seite – Rückmeldungen aus den Sektionen laufen darüber.
  onFeedback,
}) {
  const sections = SETTINGS_SECTIONS;
  const [currentSection, setCurrentSection] = useState(() => normalizeSection(activeSection, sections));
  const selectedSection = useMemo(
    () => normalizeSection(currentSection, sections),
    [currentSection, sections]
  );

  useEffect(() => {
    if (!open) return;
    setCurrentSection(normalizeSection(activeSection, sections));
  }, [activeSection, open, sections]);

  return (
    <ShiftplanDialog
      open={open}
      title="Einstellungen"
      subtitle={locationName}
      icon={<IconSettings fontSize="small" />}
      size="xl"
      bodyClassName="spv2-settings-dialog-body"
      onClose={onClose}
    >
      <Box className="spv2-settings-panel">
        <Box className="spv2-settings-nav" role="tablist" aria-label="Einstellungen">
          {sections.map((section) => {
            const isActive = selectedSection === section.id;

            return (
              <button
                key={section.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`spv2-settings-nav-item${isActive ? " is-active" : ""}`}
                onClick={() => setCurrentSection(section.id)}
              >
                <span className="spv2-settings-nav-icon">{section.icon}</span>
                <Typography component="span" className="spv2-settings-nav-label">
                  {section.label}
                </Typography>
              </button>
            );
          })}
        </Box>

        <Box className="spv2-settings-content">
          {selectedSection === "order" ? (
            <OrderSettingsContent
              active={open && selectedSection === "order"}
              initialMode={initialOrderMode}
              employees={employees}
              departments={departments}
              onSaveEmployees={onSaveEmployeeOrder}
              onResetEmployees={onResetEmployeeOrder}
              onSaveDepartments={onSaveDepartmentOrder}
              onResetDepartments={onResetDepartmentOrder}
              onFeedback={onFeedback}
            />
          ) : null}

          {selectedSection === "holidays" ? (
            <PublicHolidaysContent
              active={open && selectedSection === "holidays"}
              referenceDate={referenceDate}
              onChanged={onPublicHolidaysChanged}
              onFeedback={onFeedback}
            />
          ) : null}

          {selectedSection === "appearance" ? (
            <AppearanceSettingsContent
              active={open && selectedSection === "appearance"}
              shiftDisplayMode={shiftDisplayMode}
              displayCalendar={displayCalendar}
              departments={colorDepartments}
              shiftTemplates={shiftTemplates}
              employees={employees}
              onShiftDisplayModeChange={onShiftDisplayModeChange}
              onDisplayCalendarChange={onDisplayCalendarChange}
              onDepartmentColorChange={onDepartmentColorChange}
              onShiftColorChange={onShiftColorChange}
            />
          ) : null}
        </Box>
      </Box>
    </ShiftplanDialog>
  );
}
